import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import * as LucideIcons from "lucide-react";
import { ArrowRight, ArrowLeft, Tag, Grid3x3, MapPin } from "lucide-react";
import OfferCard from "@/components/OfferCard";
import { useSeo } from "@/hooks/useSeo";

const COLORS = {
  "restauration":              { bg: "bg-orange-50",   fg: "text-[#E05638]", chip: "bg-[#E05638]/10 text-[#E05638]", border: "border-[#E05638]" },
  "alimentation":              { bg: "bg-amber-50",    fg: "text-amber-700", chip: "bg-amber-50 text-amber-700",   border: "border-amber-500" },
  "beaute-bien-etre":          { bg: "bg-pink-50",     fg: "text-pink-600",  chip: "bg-pink-50 text-pink-600",      border: "border-pink-500" },
  "auto-moto":                 { bg: "bg-slate-100",   fg: "text-slate-700", chip: "bg-slate-100 text-slate-700",   border: "border-slate-500" },
  "loisirs-culture":           { bg: "bg-violet-50",   fg: "text-violet-700",chip: "bg-violet-50 text-violet-700",  border: "border-violet-500" },
  "sport-fitness":             { bg: "bg-emerald-50",  fg: "text-emerald-700",chip:"bg-emerald-50 text-emerald-700",border: "border-emerald-500" },
  "maison-jardin":             { bg: "bg-lime-50",     fg: "text-lime-700",  chip: "bg-lime-50 text-lime-700",      border: "border-lime-500" },
  "artisans-services":         { bg: "bg-blue-50",     fg: "text-blue-700",  chip: "bg-blue-50 text-blue-700",      border: "border-blue-500" },
  "professionnels-entreprises":{ bg: "bg-indigo-50",   fg: "text-indigo-700",chip: "bg-indigo-50 text-indigo-700",  border: "border-indigo-500" },
};

function Icon({ name, className }) {
  const I = LucideIcons[name] || Tag;
  return <I className={className}/>;
}

export default function CategoryDetail() {
  const { slug, subslug } = useParams();
  const [sp] = useSearchParams();
  const city = sp.get("city") || "";
  const [cat, setCat] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const col = COLORS[slug] || { bg: "bg-slate-50", fg: "text-slate-700", chip: "bg-slate-100 text-slate-700", border: "border-slate-500" };

  useEffect(() => {
    setLoading(true);
    api.get(`/categories/${slug}`)
      .then((r) => setCat(r.data))
      .catch(() => setCat(null));
  }, [slug]);

  useEffect(() => {
    if (!cat) return;
    const params = new URLSearchParams();
    params.set("category", cat.slug);
    if (subslug) params.set("subcategory", subslug);
    if (city) params.set("city", city);
    params.set("limit", "60");
    api.get(`/offers?${params.toString()}`)
      .then((r) => setOffers(r.data || []))
      .finally(() => setLoading(false));
  }, [cat, subslug, city]);

  const currentSub = useMemo(() => {
    if (!cat || !subslug) return null;
    return (cat.subcategories || []).find((s) => s.slug === subslug) || null;
  }, [cat, subslug]);

  const seoName = currentSub ? `${currentSub.name} · ${cat?.name || ""}` : (cat?.name || "");
  useSeo({
    title: cat
      ? `Bons plans ${seoName}${city ? ` à ${city}` : " en Eure-et-Loir"} | PROXIreduc-28`
      : "PROXIreduc-28",
    description: cat
      ? `Découvrez les meilleurs bons plans et réductions ${(seoName || "").toLowerCase()}${city ? ` à ${city}` : " en Eure-et-Loir (28)"} : ${offers.length} offre(s) locale(s) chez les commerces partenaires. Inscription gratuite sur PROXIreduc-28.`
      : "",
    canonicalPath: `/categories/${slug}${subslug ? `/${subslug}` : ""}`,
  });

  if (!cat && !loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="category-not-found">
        <h1 className="font-heading text-3xl font-extrabold">Catégorie introuvable</h1>
        <p className="mt-2 text-slate-600">La catégorie <code className="font-mono">{slug}</code> n'existe pas.</p>
        <Link to="/categories" className="mt-4 inline-flex items-center gap-1 text-[#E05638] font-semibold">
          <ArrowLeft size={14}/> Toutes les catégories
        </Link>
      </div>
    );
  }

  if (!cat) {
    return <div className="max-w-7xl mx-auto px-4 py-16 text-slate-400">Chargement…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="category-detail-page">
      {/* Fil d'ariane */}
      <nav className="text-xs text-slate-500 flex items-center gap-2" data-testid="category-breadcrumb">
        <Link to="/categories" className="hover:text-slate-800">Catégories</Link>
        <span>›</span>
        {currentSub ? (
          <>
            <Link to={`/categories/${cat.slug}`} className="hover:text-slate-800">{cat.name}</Link>
            <span>›</span>
            <span className="text-slate-800 font-semibold">{currentSub.name}</span>
          </>
        ) : (
          <span className="text-slate-800 font-semibold">{cat.name}</span>
        )}
      </nav>

      {/* Hero catégorie */}
      <div className={`mt-4 bg-white border border-slate-200 rounded-3xl p-6 md:p-8 flex items-center gap-4`}>
        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl ${col.bg} ${col.fg} grid place-items-center shrink-0`}>
          <Icon name={cat.icon} className="w-9 h-9 md:w-10 md:h-10"/>
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-slate-900" data-testid="category-title">
            {currentSub ? currentSub.name : cat.name}
          </h1>
          {!currentSub && cat.description && (
            <p className="mt-1 text-slate-600 text-sm md:text-base">{cat.description}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(offers.length > 0) && (
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${col.chip}`}>
                {offers.length} bon{offers.length > 1 ? "s" : ""} plan{offers.length > 1 ? "s" : ""} disponible{offers.length > 1 ? "s" : ""}
              </span>
            )}
            {city && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
                <MapPin size={10}/> {city}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Sous-catégories */}
      {(cat.subcategories?.length > 0) && (
        <section className="mt-8" data-testid="category-subs-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg md:text-xl font-extrabold text-slate-900">
              {currentSub ? "Autres sous-catégories" : "Sous-catégories"}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-subs-list">
            {/* Bouton "Tous" */}
            <Link to={`/categories/${cat.slug}`}
                  data-testid="subcat-pill-all"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold transition-colors ${
                    !subslug ? `${col.border} ${col.fg} bg-white` : "border-slate-200 text-slate-700 hover:border-slate-400"
                  }`}>
              <Grid3x3 size={13}/> Tous
            </Link>
            {cat.subcategories
              .filter((s) => s.slug !== (currentSub?.slug))
              .map((s) => (
                <Link key={s.id} to={`/categories/${cat.slug}/${s.slug}`}
                      data-testid={`subcat-pill-${s.slug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:border-slate-400 bg-white transition-colors">
                  {s.name}
                  {s.offer_count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${col.chip}`}>
                      {s.offer_count}
                    </span>
                  )}
                </Link>
              ))}
          </div>
        </section>
      )}

      {/* Offres */}
      <section className="mt-10" data-testid="category-offers-section">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-slate-900">
            {currentSub ? `Bons plans · ${currentSub.name}` : `Tous les bons plans · ${cat.name}`}
          </h2>
        </div>
        {loading ? (
          <div className="text-slate-400">Chargement…</div>
        ) : offers.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center" data-testid="category-offers-empty">
            <div className="text-slate-500 text-sm">
              Aucune offre active actuellement{currentSub ? " dans cette sous-catégorie" : ""}.
            </div>
            {currentSub && (
              <Link to={`/categories/${cat.slug}`} className="mt-3 inline-flex items-center gap-1 text-[#E05638] font-semibold">
                Voir tous les bons plans de {cat.name} <ArrowRight size={14}/>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {offers.map((o) => (<OfferCard key={o.id} offer={o}/>))}
          </div>
        )}
      </section>
    </div>
  );
}
